import { useCallback } from "react";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { convertISODate, convertISODateTimeHumanized } from "@entrilia/shared/utilities/dateUtils";
import adminApi from "../../../../../api/adminApi";
import useResourceChanged from "../../../../../hooks/useResourceChanged";
import ImportDataStatusTag from "../../../import-data/ImportDataStatusTag";
import { isImportOperationRunning } from "../../../import-data/importDataStateHelper";
import ContentCardLayout from "../ContentCardLayout";

interface Props {
  contentId: string;
  isOpen: boolean;
  onToggleOpen: () => void;
}

const ignoreProgress = (): void => {};

const DataImportCard = ({ contentId, isOpen, onToggleOpen }: Props) => {
  const getDetails = useCallback(() => adminApi.getDataImportDetails(contentId), [contentId]);
  const getTotals = useCallback(() => adminApi.getDataImportChangesTotals(contentId), [contentId]);

  const [dataImport, detailsError, { fetchSilent: fetchDetailsSilent }] = useFetch(getDetails);
  const [totals, totalsError, { fetchSilent: fetchTotalsSilent }] = useFetch(getTotals);

  useResourceChanged("DataImport", contentId, {
    onChanged: async () => {
      const [detailsReloadError, totalsReloadError] = await Promise.all([fetchDetailsSilent(), fetchTotalsSilent()]);
      if (detailsReloadError || totalsReloadError) {
        logError(detailsReloadError || totalsReloadError, "[DataImportCard] reload");
      }
    },
    onProgress: ignoreProgress,
    isOperationRunning: dataImport !== undefined && isImportOperationRunning(dataImport),
  });

  if (detailsError || totalsError) {
    logError(detailsError || totalsError, "[DataImportCard] load");
    return null;
  }

  if (dataImport === undefined || totals === undefined) {
    return null;
  }

  return (
    <ContentCardLayout
      title={`${dataImport.description || dataImport.sourceDescription} • ${convertISODate(dataImport.createdAt)}`}
      caption={`Data Import · ${totals.allCount.toLocaleString()} rows · Prepared ${convertISODateTimeHumanized(dataImport.createdAt, { thresholdMinutes: 60 })}`}
      status={<ImportDataStatusTag dataImportState={dataImport.state} />}
      isOpen={isOpen}
      onToggleOpen={onToggleOpen}
    />
  );
};

export default DataImportCard;
